import { v4 as uuid } from "uuid";
import { useEffect, useRef, useState } from "react";
import { get, isEmpty, isEqual } from "lodash";
import { Field, FieldArray, useFormikContext } from "formik";
import { EMPTY_STRING, EMPTY_ARRAY, INDEX } from "../../../constants";
import { redTrashIcon } from "../../../assets/icons";
import { FIELDS } from "../../../pages/DataModels/datamodels.constants";
import { ManageExpression } from "../../../pages/DataModels/ServiceFields";

const { SELECT, TEXT, EXPRESSION, CHECKBOX } = FIELDS;
const { FIRST, SECOND } = INDEX;
const ONE = 1;

const FieldsGrid = ({
  fields = EMPTY_ARRAY,
  label = EMPTY_STRING,
  name = EMPTY_STRING,
  values = EMPTY_ARRAY,
  styleClasses = {},
  handleChange = () => {},
  allValues,
  nodes,
  edges,
}) => {
  const preSelectedValues = useRef(values);
  const { errors } = useFormikContext();
  const [isCurrentFieldInvalid, setIsCurrentFieldInvalid] = useState();

  useEffect(() => {
    const isInvalid = get(errors, name, false);
    setIsCurrentFieldInvalid(!!isInvalid);
  }, [errors, name]);

  useEffect(() => {
    if (values && !isEqual(preSelectedValues.current, values)) {
      preSelectedValues.current = values;
      handleChange(values, name);
    }
  }, [values, name, handleChange]);

  const initialValues = fields.reduce((obj, field) => {
    if (field.type === CHECKBOX) return { ...obj, [field.name]: false };
    else return { ...obj, [field.name]: EMPTY_STRING };
  }, {});

  const selectTypeFields = [];
  const textTypeFields = [];
  const expressionTypeFields = [];
  const isRequiredTypeFields = [];

  fields.forEach((field) => {
    if (field.type === SELECT) selectTypeFields.push(field);
    else if (field.type === TEXT) textTypeFields.push(field);
    else if (field.type === EXPRESSION) expressionTypeFields.push(field);
    else if (field.type === CHECKBOX) isRequiredTypeFields.push(field);
  });

  return (
    <FieldArray
      name={name}
      render={(arrayHelpers) => (
        <div className="form-group">
          {values?.map((data, index) => (
            <div key={data.id} className="my-2 flex flex-col">
              <div className="flex justify-between items-center mb-1">
                <label className="text-sm font-medium w-full truncate ...">
                  {`${index + ONE}. ${data.name || label}`}
                </label>
                <button
                  className="hover:opacity-80 btn"
                  type="button"
                  onClick={() => arrayHelpers.remove(index)}
                >
                  <img src={redTrashIcon} alt="delete" className="w-6 h-6" />
                </button>
              </div>
              <div
                className={styleClasses.textFields || styleClasses.orderSecond}
              >
                {textTypeFields.map((field) => (
                  <Field
                    key={field.name}
                    type={TEXT}
                    name={`${name}[${index}].${field.name}`}
                    value={data[field.name]}
                    className="form-control mb-1"
                    placeholder={
                      field.name.charAt(FIRST).toUpperCase() +
                      field.name.slice(SECOND)
                    }
                  />
                ))}
              </div>
              <div className={styleClasses.expressionFields}>
                {expressionTypeFields.map((field) => (
                  <div key={field.name}>
                    <ManageExpression
                      name={field.name}
                      values={allValues}
                      nodes={nodes}
                      edges={edges}
                      expressionKey={`${name}[${index}].${field.name}`}
                      handleChange={handleChange}
                      className="!h-10"
                    />
                  </div>
                ))}
              </div>
              <div
                className={styleClasses.selectFields || styleClasses.orderFirst}
              >
                {selectTypeFields.map((field) => (
                  <Field
                    key={field.name}
                    as={SELECT}
                    className={`capitalize form-control`}
                    name={`${name}[${index}].${field.name}`}
                    value={data[field.name]}
                  >
                    <option key={name} value={EMPTY_STRING}>
                      Select option
                    </option>

                    {field.options.map((option) =>
                      typeof option === "string" ? (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ) : (
                        <option
                          key={option.id}
                          value={option.refName || option.name}
                        >
                          {option.name}
                        </option>
                      )
                    )}
                  </Field>
                ))}
              </div>
              <div className={styleClasses.isRequiredFields}>
                {isRequiredTypeFields.map((field) => (
                  <label key={field.name} className="flex items-center">
                    <Field
                      type="checkbox"
                      name={`${name}[${index}].${field.name}`}
                      checked={data[field.name]}
                      value={data[field.name]}
                      className={
                        "text-indigo-600 rounded-full focus:ring-0 mt-1 ml-1"
                      }
                    />
                    <div className="ml-1.5 mt-1">
                      <h6 className="text-sm font-normal">Required</h6>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ))}
          <div className="form-group">
            <div className="text-sm font-medium text-gray-50 flex flex-row my-1 form-control">
              <button
                type="button"
                onClick={() =>
                  arrayHelpers.push({
                    id: uuid(),
                    ...initialValues,
                  })
                }
                disabled={isCurrentFieldInvalid && !isEmpty(values)}
                className="rounded items-center btn btn-primary-light w-full h-fit"
              >
                {`Add New ${label}`}
              </button>
            </div>
          </div>
        </div>
      )}
    />
  );
};

export default FieldsGrid;
